import type { LeagueDetail, LeagueSummary, ScoringRule } from "@/modules/leagues/types/league";
import { getCurrentSeason } from "@/modules/leagues/types/league";
import type { Match, ParticipantPrediction, TeamRef } from "@/modules/leagues/types/match";

/** Regras de pontuação padrão do sistema — usadas no wizard e aqui nos mocks. */
export const DEFAULT_SCORING_RULES: ScoringRule[] = [
  { id: "placar_exato", label: "Placar Exato", points: 120 },
  { id: "empate", label: "Empate (sem placar exato)", points: 80 },
  { id: "vencedor_gols", label: "Vencedor + gols do vencedor", points: 60 },
  { id: "vencedor_gols_perdedor", label: "Vencedor + gols do perdedor", points: 35 },
  { id: "vencedor", label: "Apenas o vencedor", points: 25 },
  { id: "saldo", label: "Saldo de gols", points: 10 },
];

const TEAMS: TeamRef[] = [
  { id: "flamengo", name: "Flamengo", shortName: "FLA", crestUrl: null },
  { id: "palmeiras", name: "Palmeiras", shortName: "PAL", crestUrl: null },
  { id: "corinthians", name: "Corinthians", shortName: "COR", crestUrl: null },
  { id: "sao-paulo", name: "São Paulo", shortName: "SPFC", crestUrl: null },
  { id: "gremio", name: "Grêmio", shortName: "GRE", crestUrl: null },
  { id: "internacional", name: "Internacional", shortName: "INT", crestUrl: null },
  { id: "atletico-mg", name: "Atlético-MG", shortName: "CAM", crestUrl: null },
  { id: "botafogo", name: "Botafogo", shortName: "BOT", crestUrl: null },
  { id: "fluminense", name: "Fluminense", shortName: "FLU", crestUrl: null },
  { id: "vasco", name: "Vasco da Gama", shortName: "VAS", crestUrl: null },
];

function hoursFromNow(h: number): string {
  return new Date(Date.now() + h * 60 * 60 * 1000).toISOString();
}

function teamAt(index: number): TeamRef {
  const team = TEAMS[index % TEAMS.length];
  if (!team) throw new Error("Índice de time mock inválido");
  return team;
}

function pair(i: number): [TeamRef, TeamRef] {
  return [teamAt(i), teamAt(i + 3)];
}

interface RoundRow {
  offsetHours: number;
  status: Match["status"];
  home?: number;
  away?: number;
  userScore?: [number, number];
  points?: number | null;
}

const CURRENT_ROUND_ROWS: RoundRow[] = [
  { offsetHours: -48, status: "finished", home: 2, away: 1, userScore: [2, 1], points: 120 },
  { offsetHours: -30, status: "finished", home: 0, away: 0, userScore: [1, 0], points: 25 },
  { offsetHours: -0.5, status: "live", home: 1, away: 1 },
  { offsetHours: 6, status: "scheduled" },
  { offsetHours: 30, status: "scheduled" },
  { offsetHours: 54, status: "scheduled" },
];

/** Gera a rodada atual de uma liga com estados variados e realistas (datas sempre relativas a "agora"). */
function buildCurrentRoundMatches(leagueId: string, competitionId: string, includeUserPredictions: boolean): Match[] {
  return CURRENT_ROUND_ROWS.map((row, idx) => {
    const [home, away] = pair(idx);
    const kickoff = hoursFromNow(row.offsetHours);
    const predictionsLocked = row.status !== "scheduled";
    const userPrediction =
      includeUserPredictions && row.userScore
        ? {
            homeScore: row.userScore[0],
            awayScore: row.userScore[1],
            submittedAt: hoursFromNow(row.offsetHours - 30),
            pointsEarned: row.points ?? null,
            isOfficial: (row.points ?? null) !== null,
          }
        : null;

    const match: Match = {
      id: `${leagueId}-r15-m${idx + 1}`,
      leagueId,
      round: 15,
      roundLabel: "Rodada 15",
      stage: null,
      competitionId,
      homeTeam: home,
      awayTeam: away,
      kickoff,
      stadium: "Estádio Municipal",
      status: row.status,
      homeScore: row.status === "scheduled" ? null : row.home ?? null,
      awayScore: row.status === "scheduled" ? null : row.away ?? null,
      userPrediction,
      predictionsLocked,
    };
    return match;
  });
}

const LEAGUE_AMIGOS_ID = "liga-amigos-brasileirao";
const LEAGUE_TRABALHO_ID = "liga-trabalho-libertadores";

const amigosMatches = buildCurrentRoundMatches(LEAGUE_AMIGOS_ID, "brasileirao", true);
const trabalhoMatches = buildCurrentRoundMatches(LEAGUE_TRABALHO_ID, "libertadores", false);

export const MOCK_MATCHES_BY_LEAGUE: Record<string, Match[]> = {
  [LEAGUE_AMIGOS_ID]: amigosMatches,
  [LEAGUE_TRABALHO_ID]: trabalhoMatches,
};

export const MOCK_LEAGUES: LeagueDetail[] = [
  {
    id: LEAGUE_AMIGOS_ID,
    nome: "Liga dos Amigos",
    competitionId: "brasileirao",
    competitionCode: "brasileirao",
    competitionName: "Brasileirão",
    season: getCurrentSeason(),
    bannerUrl: null,
    participantsCount: 12,
    status: "em_andamento",
    currentRoundLabel: "Rodada 15",
    pendingPredictionsCount: amigosMatches.filter((m) => m.status === "scheduled" && !m.userPrediction).length,
    hasLiveMatch: amigosMatches.some((m) => m.status === "live"),
    inviteCode: "AMIGOS27",
    myRole: "admin",
    createdAt: hoursFromNow(-24 * 40),
    descricao: "Bolão da galera do bairro, todo mundo palpitando desde 2019.",
    maxParticipantes: 30,
    privada: true,
    permiteDesafios: true,
    permitePerguntasBonus: true,
    permiteImportacaoManual: true,
    permiteEntradaAposInicio: true,
    disabledClubIds: [],
    rules: { frozenAt: hoursFromNow(-24 * 40), rules: DEFAULT_SCORING_RULES },
  },
  {
    id: LEAGUE_TRABALHO_ID,
    nome: "Bolão do Trabalho",
    competitionId: "libertadores",
    competitionCode: "libertadores",
    competitionName: "Libertadores",
    season: getCurrentSeason(),
    bannerUrl: null,
    participantsCount: 24,
    status: "em_andamento",
    currentRoundLabel: "Oitavas de Final",
    pendingPredictionsCount: trabalhoMatches.filter((m) => m.status === "scheduled").length,
    hasLiveMatch: trabalhoMatches.some((m) => m.status === "live"),
    inviteCode: "TRAB2027",
    myRole: "member",
    createdAt: hoursFromNow(-24 * 15),
    descricao: null,
    maxParticipantes: 50,
    privada: false,
    permiteDesafios: false,
    permitePerguntasBonus: true,
    permiteImportacaoManual: true,
    permiteEntradaAposInicio: true,
    disabledClubIds: [],
    rules: { frozenAt: hoursFromNow(-24 * 15), rules: DEFAULT_SCORING_RULES },
  },
];

const PARTICIPANT_NAMES = ["Rodrigo Alves", "Camila Souza", "Bruno Lima", "Ana Paula", "Diego Ferreira", "Juliana Costa"];

export function buildMockParticipantPredictions(match: Match): ParticipantPrediction[] {
  if (match.status === "scheduled") return [];
  return PARTICIPANT_NAMES.map((nome, idx) => {
    const homeScore = (match.homeScore ?? 0) + (idx % 3 === 0 ? 0 : idx % 2);
    const awayScore = (match.awayScore ?? 0) + (idx % 4 === 0 ? 1 : 0);
    const acertouPlacar = match.status === "finished" && homeScore === match.homeScore && awayScore === match.awayScore;
    const acertouVencedor =
      match.status === "finished" &&
      Math.sign(homeScore - awayScore) === Math.sign((match.homeScore ?? 0) - (match.awayScore ?? 0));
    const prediction: ParticipantPrediction = {
      uid: `mock-${idx}`,
      nome,
      avatarUrl: null,
      homeScore,
      awayScore,
      pointsEarned: match.status === "finished" ? (acertouPlacar ? 120 : acertouVencedor ? 25 : 0) : null,
      isOfficial: match.status === "finished",
      acertouPlacar,
      acertouVencedor,
    };
    return prediction;
  });
}

export function toSummary(league: LeagueDetail): LeagueSummary {
  return {
    id: league.id,
    nome: league.nome,
    competitionId: league.competitionId,
    competitionCode: league.competitionCode,
    competitionName: league.competitionName,
    season: league.season,
    bannerUrl: league.bannerUrl,
    participantsCount: league.participantsCount,
    status: league.status,
    currentRoundLabel: league.currentRoundLabel,
    pendingPredictionsCount: league.pendingPredictionsCount,
    hasLiveMatch: league.hasLiveMatch,
    inviteCode: league.inviteCode,
    myRole: league.myRole,
    createdAt: league.createdAt,
  };
}
